import { Fact } from '../Fact'
import { RatingButtons } from '../RatingButtons'
import { RATING_LABEL } from '../ratingColors'
import { calendarDate, relativeDay, spanLabel, type LabNode } from './labGeometry'
import type { LabPlan } from '@/types/admin'

interface LabInspectorProps {
  node: LabNode
  plan: LabPlan
  onRate: (index: number, rating: number) => void
}

/**
 * Everything the scheduler decided at one review, beside what the saved settings
 * would have decided — and, for a planned review, the rating to script there.
 */
export function LabInspector({ node, plan, onRate }: LabInspectorProps) {
  if (node.kind !== 'plan') {
    const past = node.kind === 'past'
    return (
      <div className="rounded-md border border-border-divider bg-surface-bg/60 p-5">
        <p className="font-mono text-xs uppercase tracking-widest text-muted">
          {past ? `Recorded review · ${calendarDate(node.day)}` : 'Starting strength'}
        </p>
        <p className="mt-2 font-serif text-base text-body">
          {past
            ? `${relativeDay(node.day)}, rated ${RATING_LABEL[node.rating ?? 3]}, which sent it ${spanLabel(node.interval)} ahead.`
            : `The review that set this word ${spanLabel(node.interval)} ahead, so it falls due today.`}
        </p>
      </div>
    )
  }

  const step = plan.steps[node.index]
  const saved = plan.baseline[node.index]

  return (
    <div className="rounded-md border border-border-divider bg-surface-bg/60 p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="font-serif text-lg text-heading">
          Review {step.review}
          <span className="ml-3 font-mono text-xs uppercase tracking-widest text-muted">
            {relativeDay(step.day)} · {calendarDate(step.day)}
          </span>
        </p>
        {!step.scripted && (
          <p className="font-serif text-sm text-muted">Assumed Good. Pick a rating to script it.</p>
        )}
      </div>

      <div className="mt-4">
        <RatingButtons
          label={`Rating for review ${step.review}`}
          selected={step.scripted ? step.rating : null}
          onRate={(rating) => onRate(node.index, rating)}
        />
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
        <Fact label="Next in" value={spanLabel(step.interval_days)} saved={saved && spanLabel(saved.interval_days)} />
        <Fact label="Ease" value={step.ease_factor.toFixed(2)} saved={saved?.ease_factor.toFixed(2)} />
        <Fact label="Status" value={step.status} saved={saved?.status} />
        <Fact
          label="Slip memory"
          value={step.lapsed_interval_days ? spanLabel(step.lapsed_interval_days) : 'none'}
        />
      </dl>
    </div>
  )
}
